// ═══════════════════════════════════════════════════════════════
// documentImport.ts – fájl tartalmának kinyerése formázott leíráshoz
//
// PDF (pdf.js): sorok és bekezdések visszaépítése, félkövér betű, a PDF-ben lévő
//               hivatkozások (link-annotációk) és a szövegbeli URL-ek / e-mail-címek.
// DOCX (mammoth), TXT (UTF-8 / Windows-1250), HTML.
// Az első blokk, ha címsornak látszik (félkövér / nagyobb betű / pontszámmal kezdődik /
// egyezik a pont címével), külön jön vissza – a felület alapból elhagyja.
// A kimenet mindig szűrt HTML (sanitizeHtml).
// ═══════════════════════════════════════════════════════════════
import { sanitizeHtml } from './sanitizeHtml';

export interface ImportedDocument {
  /** a címsornak felismert első blokk (HTML) – üres, ha nincs */
  headingHtml: string;
  headingText: string;
  /** a többi tartalom (HTML) */
  bodyHtml: string;
  warnings: string[];
  pages?: number;
  kind: 'pdf' | 'docx' | 'txt' | 'html';
}

export const IMPORTABLE_EXT = ['pdf', 'docx', 'txt', 'html', 'htm'];
export const isImportable = (filename: string) => IMPORTABLE_EXT.includes(String(filename).split('.').pop()?.toLowerCase() || '');

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const LINKIFY = /(https?:\/\/[^\s<>"]+[^\s<>".,;:!?)]|www\.[^\s<>"]+[^\s<>".,;:!?)]|[\w.+-]+@[\w-]+(\.[\w-]+)+)/g;
const linkify = (escaped: string) => escaped.replace(LINKIFY, (m) => {
  if (m.includes('@') && !/^(https?:|www\.)/.test(m)) return `<a href="mailto:${m}">${m}</a>`;
  return `<a href="${m.startsWith('www.') ? 'https://' + m : m}">${m}</a>`;
});
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const PONT_START = /^\s*\d+(\.\d+)*\.?\s+\S/;

/** a szöveg a pont címének / számának látszik-e */
function looksLikeTitle(text: string, title?: string, pont?: string): boolean {
  const t = norm(text);
  if (!t) return false;
  if (pont && t.startsWith(norm(pont) + ' ')) return true;
  if (title) {
    const n = norm(title);
    const probe = n.slice(0, Math.min(40, n.length));
    if (probe.length >= 12 && (t.includes(probe) || n.includes(t.slice(0, 40)))) return true;
  }
  return PONT_START.test(text) && text.length < 400;
}

// ─────────────────────────────── PDF ───────────────────────────────
type PdfRun = { text: string; bold: boolean; url?: string; size: number };
type PdfLine = { y: number; x0: number; x1: number; size: number; runs: PdfRun[]; page: number };

async function loadPdfjs() {
  // „legacy” build: régebbi böngészőkben is fut (a normál build pl. Math.sumPrecise-t igényel)
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  const worker = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

async function extractPdf(data: ArrayBuffer, title?: string, pont?: string): Promise<ImportedDocument> {
  const pdfjs = await loadPdfjs();
  const doc = await pdfjs.getDocument({ data, disableFontFace: true, verbosity: 0 }).promise;
  const lines: PdfLine[] = [];
  const warnings: string[] = [];

  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const [content, annots] = await Promise.all([page.getTextContent(), page.getAnnotations()]);
    // a betűkészletek valódi neve (pl. „TimesNewRomanPS-BoldMT”) csak a műveletlista után érhető el
    try { await page.getOperatorList(); } catch { /* */ }
    const fontBold = new Map<string, boolean>();
    const isBold = (fontName: string) => {
      if (fontBold.has(fontName)) return fontBold.get(fontName)!;
      let b = false;
      try {
        const f: any = (page as any).commonObjs.has(fontName) ? (page as any).commonObjs.get(fontName) : null;
        const nm = String(f?.name || f?.loadedName || '') + ' ' + String((content.styles as any)[fontName]?.fontFamily || '');
        b = /bold|black|heavy|semibold|demi/i.test(nm) || Boolean(f?.bold);
      } catch { /* */ }
      fontBold.set(fontName, b);
      return b;
    };
    const links = (annots as any[])
      .filter((a) => a.subtype === 'Link' && (a.url || a.unsafeUrl))
      .map((a) => ({ url: String(a.url || a.unsafeUrl), r: a.rect as number[] }))
      .filter((l) => /^(https?:|mailto:|tel:)/i.test(l.url));
    const linkAt = (x: number, y: number) => links.find((l) => x >= Math.min(l.r[0], l.r[2]) - 1 && x <= Math.max(l.r[0], l.r[2]) + 1 && y >= Math.min(l.r[1], l.r[3]) - 2 && y <= Math.max(l.r[1], l.r[3]) + 2)?.url;

    const pageLines: PdfLine[] = [];
    for (const it of content.items as any[]) {
      if (typeof it.str !== 'string') continue;
      const str = it.str;
      if (!str && !it.hasEOL) continue;
      const x = it.transform[4];
      const y = it.transform[5];
      const size = Math.abs(it.transform[3]) || it.height || 10;
      const w = it.width || 0;
      let line = pageLines.find((l) => Math.abs(l.y - y) < size * 0.45);
      if (!line) { line = { y, x0: x, x1: x + w, size, runs: [], page: p }; pageLines.push(line); }
      if (!str.trim() && !str) continue;
      const run: PdfRun & { x: number; w: number } = { text: str, bold: isBold(it.fontName), url: linkAt(x + w / 2, y + size / 3), size, x, w } as any;
      (line.runs as any[]).push(run);
      line.x0 = Math.min(line.x0, x); line.x1 = Math.max(line.x1, x + w); line.size = Math.max(line.size, size);
    }
    for (const l of pageLines) {
      // balról jobbra, a szavak közé szóköz, ha a PDF-ben rés van
      const rs = (l.runs as any[]).sort((a, b) => a.x - b.x);
      const merged: PdfRun[] = [];
      let prevEnd: number | null = null;
      for (const r of rs) {
        let text = r.text;
        if (prevEnd !== null && r.x - prevEnd > r.size * 0.2 && !/\s$/.test(merged[merged.length - 1]?.text || '') && !/^\s/.test(text)) text = ' ' + text;
        const last = merged[merged.length - 1];
        if (last && last.bold === r.bold && last.url === r.url) last.text += text;
        else merged.push({ text, bold: r.bold, url: r.url, size: r.size });
        prevEnd = r.x + r.w;
      }
      l.runs = merged.filter((r) => r.text.length);
    }
    lines.push(...pageLines.filter((l) => l.runs.some((r) => r.text.trim())).sort((a, b) => b.y - a.y));
    page.cleanup();
  }

  if (!lines.length) {
    return { headingHtml: '', headingText: '', bodyHtml: '', warnings: ['A PDF-ben nincs kinyerhető szöveg (valószínűleg beszkennelt kép) – ilyenkor csatolmányként érdemes feltölteni.'], pages: doc.numPages, kind: 'pdf' };
  }

  // bekezdések: nagyobb függőleges rés, betűméret- vagy félkövérség-váltás → új blokk
  const sizes = lines.map((l) => l.size).sort((a, b) => a - b);
  const bodySize = sizes[Math.floor(sizes.length / 2)];
  const gaps: number[] = [];
  for (let i = 1; i < lines.length; i++) if (lines[i].page === lines[i - 1].page) gaps.push(lines[i - 1].y - lines[i].y);
  const lineGap = gaps.length ? gaps.sort((a, b) => a - b)[Math.floor(gaps.length / 2)] : bodySize * 1.2;
  const lineBold = (l: PdfLine) => l.runs.filter((r) => r.text.trim()).every((r) => r.bold);
  // a jobb margó a törzsszövegből (a középre igazított, félkövér címsor ne torzítsa); 90. percentilis
  const bodyX1 = lines.filter((l) => !lineBold(l) && l.size <= bodySize * 1.15).map((l) => l.x1).sort((a, b) => a - b);
  const rightEdge = bodyX1.length ? bodyX1[Math.min(bodyX1.length - 1, Math.floor(bodyX1.length * 0.9))] : Math.max(...lines.map((l) => l.x1));

  type Block = { lines: PdfLine[]; heading: boolean };
  const blocks: Block[] = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    const prev = lines[i - 1];
    const big = l.size > bodySize * 1.15;
    const startNew = !prev || prev.page !== l.page
      || (prev.y - l.y) > Math.max(lineGap * 1.45, l.size * 1.6)
      || lineBold(prev) !== lineBold(l)
      || Math.abs(prev.size - l.size) > bodySize * 0.15;
    if (startNew) blocks.push({ lines: [l], heading: big || lineBold(l) });
    else blocks[blocks.length - 1].lines.push(l);
  }

  const runHtml = (r: PdfRun) => {
    let h = r.url ? esc(r.text) : linkify(esc(r.text));
    if (r.url) h = `<a href="${esc(r.url)}">${h}</a>`;
    if (r.bold) h = `<strong>${h}</strong>`;
    return h;
  };
  const blockHtml = (b: Block) => {
    let out = '';
    b.lines.forEach((l, i) => {
      const txt = l.runs.map(runHtml).join('').replace(/\s+$/, '');
      if (i === 0) { out += txt.replace(/^\s+/, ''); return; }
      const prev = b.lines[i - 1];
      // a sor végéig érő előző sor folyó szöveg (tördelés) → szóköz; különben sortörés
      const wrapped = prev.x1 > rightEdge * 0.85 && Math.abs(l.x0 - b.lines[0].x0) < bodySize * 2;
      const hyphen = /[a-záéíóöőúüű]-$/i.test(out) && wrapped;
      out = hyphen ? out.slice(0, -1) + txt.replace(/^\s+/, '') : out + (wrapped ? ' ' : '<br>') + txt.replace(/^\s+/, '');
    });
    if (b.heading && !b.lines.every(lineBold)) return `<h3>${out}</h3>`;
    return `<p>${out}</p>`;
  };
  const blockText = (b: Block) => b.lines.map((l) => l.runs.map((r) => r.text).join('')).join(' ').replace(/\s+/g, ' ').trim();

  let headIdx = -1;
  if (blocks.length > 1) {
    const first = blocks[0];
    if (first.heading || looksLikeTitle(blockText(first), title, pont)) headIdx = 0;
  }
  const head = headIdx === 0 ? blocks[0] : null;
  const body = blocks.slice(head ? 1 : 0);
  if (doc.numPages > 30) warnings.push(`A PDF ${doc.numPages} oldalas – hosszú dokumentumot inkább csatolmányként érdemes közzétenni.`);
  return {
    headingHtml: head ? sanitizeHtml(blockHtml(head)) : '',
    headingText: head ? blockText(head) : '',
    bodyHtml: sanitizeHtml(body.map(blockHtml).join('')),
    warnings,
    pages: doc.numPages,
    kind: 'pdf',
  };
}

// ───────────────────────── HTML / DOCX / TXT ─────────────────────────
function splitHtmlHeading(html: string, title?: string, pont?: string) {
  const clean = sanitizeHtml(html);
  const doc = new DOMParser().parseFromString(`<body>${clean}</body>`, 'text/html');
  const blocks = Array.from(doc.body.children).filter((el) => (el.textContent || '').trim() || el.querySelector('img,table'));
  const first = blocks[0];
  if (!first || blocks.length < 2) return { headingHtml: '', headingText: '', bodyHtml: clean };
  const text = (first.textContent || '').trim();
  const tag = first.tagName.toLowerCase();
  const allBold = !!text && Array.from(first.querySelectorAll('strong,b')).map((b) => b.textContent || '').join('').trim().length >= text.length * 0.9;
  if (/^h[1-4]$/.test(tag) || allBold || looksLikeTitle(text, title, pont)) {
    const headingHtml = first.outerHTML;
    first.remove();
    return { headingHtml, headingText: text, bodyHtml: doc.body.innerHTML };
  }
  return { headingHtml: '', headingText: '', bodyHtml: clean };
}

function decodeText(buf: ArrayBuffer): string {
  const utf8 = new TextDecoder('utf-8').decode(buf);
  if (!utf8.includes('�')) return utf8.replace(/^﻿/, '');
  try { return new TextDecoder('windows-1250').decode(buf); } catch { return utf8; }
}

function textToHtml(text: string) {
  return text.replace(/\r\n?/g, '\n').split(/\n{2,}/).filter((p) => p.trim())
    .map((p) => `<p>${p.split('\n').map((l) => linkify(esc(l))).join('<br>')}</p>`).join('');
}

/**
 * @param file a fájl (vagy letöltött melléklet) – a név a típus felismeréséhez kell
 */
export async function extractDocument(file: Blob, filename: string, opts: { title?: string; pont?: string } = {}): Promise<ImportedDocument> {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const buf = await file.arrayBuffer();
  if (ext === 'pdf' || file.type === 'application/pdf') return extractPdf(buf, opts.title, opts.pont);
  if (ext === 'docx') {
    const mammoth: any = await import('mammoth');
    const res = await (mammoth.default || mammoth).convertToHtml({ arrayBuffer: buf });
    const s = splitHtmlHeading(res.value || '', opts.title, opts.pont);
    const warnings = (res.messages || []).filter((m: any) => m.type === 'error').map((m: any) => String(m.message)).slice(0, 3);
    return { ...s, warnings, kind: 'docx' };
  }
  if (ext === 'html' || ext === 'htm') {
    const raw = decodeText(buf);
    const bodyMatch = raw.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return { ...splitHtmlHeading(bodyMatch ? bodyMatch[1] : raw, opts.title, opts.pont), warnings: [], kind: 'html' };
  }
  if (ext === 'txt' || file.type.startsWith('text/')) {
    return { ...splitHtmlHeading(textToHtml(decodeText(buf)), opts.title, opts.pont), warnings: [], kind: 'txt' };
  }
  throw new Error('Ez a fájltípus nem tölthető be tartalomként (PDF, DOCX, TXT, HTML lehet).');
}
